import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Camera, X, SwitchCamera, Zap, MapPin, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface LiveCameraCaptureProps {
  onCapture?: (imageData: string) => void;
  onClose?: () => void;
  damageType?: string;
  jobId?: string;
}

export function LiveCameraCapture({ onCapture, onClose, damageType, jobId }: LiveCameraCaptureProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [facingMode, setFacingMode] = useState<"user" | "environment">("environment");
  const [isScanning, setIsScanning] = useState(false);
  const [gpsLocation, setGpsLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [scanResult, setScanResult] = useState<string | null>(null);

  useEffect(() => {
    startCamera();
    getLocation();

    return () => {
      stopCamera();
    };
  }, [facingMode]);

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode,
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
      });

      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
      setStream(mediaStream);
    } catch (error) {
      console.error("Camera access error:", error);
      toast.error("Unable to access camera. Please check permissions.");
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
    }
  };

  const getLocation = () => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setGpsLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        },
        (error) => {
          console.error("GPS error:", error);
        }
      );
    }
  };

  const switchCamera = () => {
    stopCamera();
    setFacingMode(facingMode === "user" ? "environment" : "user");
  };

  const capturePhoto = async () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const context = canvas.getContext("2d");
    if (!context) return;

    context.drawImage(video, 0, 0);
    const imageData = canvas.toDataURL("image/jpeg", 0.9);

    // Trigger AI analysis
    await analyzeImage(imageData);

    if (onCapture) {
      onCapture(imageData);
    }

    // Haptic feedback if available
    if ("vibrate" in navigator) {
      navigator.vibrate(50);
    }

    toast.success("Photo captured!");
  };

  const analyzeImage = async (imageData: string) => {
    setIsScanning(true);
    setScanResult(null);

    try {
      // Use the existing analyze-damage edge function with base64 image
      const { data, error } = await supabase.functions.invoke("analyze-damage", {
        body: {
          photoUrl: imageData, // Pass the data URL directly
          orgId: jobId, // Optional org context
          photoId: `temp-${Date.now()}`, // Temporary ID for live capture
        },
      });

      if (error) throw error;

      if (data?.analysis) {
        const { damageTypes, severity, confidenceScore, findings } = data.analysis;
        const resultText = `Severity: ${severity.toUpperCase()}\n${findings.join("\n")}`;
        setScanResult(resultText);
        toast.success("AI Analysis Complete!", {
          description: `Found: ${damageTypes.join(", ") || "No damage"}`,
        });
      }
    } catch (error) {
      console.error("AI analysis error:", error);
      toast.error("Analysis failed. Photo saved for later processing.");
      setScanResult("Analysis failed - photo saved locally");
    } finally {
      setIsScanning(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black">
      {/* Video Feed */}
      <video
        ref={videoRef}
        autoPlay
        playsInline
        className="absolute inset-0 w-full h-full object-cover"
      />

      <canvas ref={canvasRef} className="hidden" />

      {/* Top Controls */}
      <div className="absolute top-0 left-0 right-0 z-10 bg-gradient-to-b from-black/60 to-transparent p-4">
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="text-white hover:bg-white/20"
          >
            <X className="h-6 w-6" />
          </Button>

          {gpsLocation && (
            <div className="flex items-center gap-2 bg-black/40 backdrop-blur-sm px-3 py-2 rounded-full">
              <MapPin className="h-4 w-4 text-green-400" />
              <span className="text-xs text-white font-medium">GPS Active</span>
            </div>
          )}

          <Button
            variant="ghost"
            size="icon"
            onClick={switchCamera}
            className="text-white hover:bg-white/20"
          >
            <SwitchCamera className="h-6 w-6" />
          </Button>
        </div>

        {/* AI Scanning Indicator */}
        {isScanning && (
          <div className="mt-4 bg-primary/90 backdrop-blur-sm px-4 py-3 rounded-xl flex items-center gap-3">
            <Loader2 className="h-5 w-5 animate-spin text-white" />
            <div className="flex-1">
              <div className="text-white font-semibold text-sm">AI Analyzing...</div>
              <div className="text-white/80 text-xs">Detecting damage patterns</div>
            </div>
          </div>
        )}

        {/* Scan Result */}
        {scanResult && (
          <Card className="mt-4 p-4 bg-black/60 backdrop-blur-sm border-primary/50">
            <div className="flex items-start gap-3">
              <Zap className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="text-white font-semibold text-sm mb-1">AI Detection</div>
                <div className="text-white/90 text-sm">{scanResult}</div>
              </div>
            </div>
          </Card>
        )}
      </div>

      {/* Bottom Controls */}
      <div className="absolute bottom-0 left-0 right-0 z-10 bg-gradient-to-t from-black/60 to-transparent p-8">
        <div className="flex items-center justify-center">
          <Button
            size="icon"
            onClick={capturePhoto}
            disabled={isScanning}
            className="h-20 w-20 rounded-full shadow-2xl ring-4 ring-white/30 hover:scale-105 transition-transform"
          >
            <Camera className="h-10 w-10" />
          </Button>
        </div>

        {damageType && (
          <div className="mt-4 text-center">
            <div className="text-white/60 text-xs mb-1">Scanning for</div>
            <div className="text-white font-semibold">{damageType}</div>
          </div>
        )}
      </div>

      {/* Scan Grid Overlay */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="w-full h-full border-2 border-primary/30 animate-pulse" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 border-2 border-primary/50 rounded-lg" />
      </div>
    </div>
  );
}
